import { connectRealtime, type RealtimeHandlers } from '@restaurant/api-client';
import { realtimeCacheHandlers } from '@restaurant/api-client/react';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { API_URL, api, getAccessToken } from './api';
import { useAuth } from './auth';
import { notify, playChime } from './platform';

type Listener = Partial<RealtimeHandlers>;
interface RealtimeState {
  connected: boolean;
  /** Extra handlers for a screen (e.g. AI suggestions for the open chat). Returns an unsubscribe fn. */
  subscribe(l: Listener): () => void;
}
const RealtimeContext = createContext<RealtimeState>({ connected: false, subscribe: () => () => undefined });

/** One socket per session: keeps React Query caches live and raises sounds/notifications for new work. */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    if (status !== 'authenticated') return;
    const cache = realtimeCacheHandlers(qc);
    const fanout = <K extends keyof RealtimeHandlers>(key: K, ...args: Parameters<NonNullable<RealtimeHandlers[K]>>) => {
      listeners.current.forEach((l) => (l[key] as ((...a: unknown[]) => void) | undefined)?.(...args));
    };
    const socket = connectRealtime({
      baseUrl: API_URL,
      getToken: getAccessToken,
      refresh: () => api.auth.refresh(),
      handlers: {
        ...cache,
        onConnectionChange: setConnected,
        onMessage: (m) => {
          cache.onMessage?.(m);
          fanout('onMessage', m);
          if (m.direction === 'inbound' && m.type !== 'reaction') {
            playChime();
            void notify('New WhatsApp message', m.text ?? `[${m.type}]`);
          }
        },
        onMessageStatus: (m) => {
          cache.onMessageStatus?.(m);
          fanout('onMessageStatus', m);
        },
        onConversation: (c) => {
          cache.onConversation?.(c);
          fanout('onConversation', c);
        },
        onAiDraft: (o) => {
          cache.onAiDraft?.(o);
          fanout('onAiDraft', o);
          playChime();
          toast.info(`New AI draft order #${o.number}`, { description: `${o.customer.name ?? o.customer.waId} · ${o.items.length} item(s)` });
          void notify(`New order draft #${o.number}`, o.items.map((i) => `${i.quantity}× ${i.name}`).join(', '));
        },
        onOrderCreated: (o) => {
          cache.onOrderCreated?.(o);
          fanout('onOrderCreated', o);
        },
        onOrderUpdated: (o) => {
          cache.onOrderUpdated?.(o);
          fanout('onOrderUpdated', o);
        },
        onAiSuggestions: (d) => {
          cache.onAiSuggestions?.(d);
          fanout('onAiSuggestions', d);
        },
        onTemplateStatus: (d) => {
          cache.onTemplateStatus?.(d);
          toast(`Template "${d.name}" is now ${d.status.toLowerCase()}`);
        },
      },
    });
    return () => {
      socket.close();
      setConnected(false);
    };
  }, [status, qc]);

  const subscribe = (l: Listener) => {
    listeners.current.add(l);
    return () => {
      listeners.current.delete(l);
    };
  };
  return <RealtimeContext.Provider value={{ connected, subscribe }}>{children}</RealtimeContext.Provider>;
}

export const useRealtime = () => useContext(RealtimeContext);
